import type React from "react"
import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { Button, Dropdown, Menu, message, Divider } from "antd"
import {
    MoreOutlined,
    QuestionCircleOutlined
} from "@ant-design/icons"
import logo from "../../../shared/assets/images/CQLogo.svg"
import LazyLoader from "../../../shared/components/LazyLoader/load"
import { useMedia } from "../context/MediaContext";
import { useCheckAiInterviewInvitationQuery } from "../slice/aiInterviewApiSlice";
import InvitationNotValid from "./InvitationNotValid";
import { ReactComponent as CameraOutlined } from "../../../shared/assets/images/VideoCamera.svg";
import { ReactComponent as AudioOutlined } from "../../../shared/assets/images/Microphone.svg";
import { ReactComponent as SoundOutlined } from "../../../shared/assets/images/speakers.svg";
import { ReactComponent as MutedCameraOutlined } from "../../../shared/assets/images/mutedVideocamera.svg";
import { ReactComponent as AudioMutedOutlined } from "../../../shared/assets/images/MicrophoneOff.svg";
import { ReactComponent as Microphone } from "../../../shared/assets/images/microphoneBlue.svg"
import { ReactComponent as Lightbulb } from "../../../shared/assets/images/Lightbulb.svg"
import { useDispatch } from "react-redux"
import { setJobTitle,setUserName } from "../slice/aiInterviewSlice"
import ModalComponent from "../../../shared/components/Modals/ModalComponent"
import { AnimatedSpeaking } from "./animatedSpeaking"

const normaliseStatus = (status: unknown): string | undefined => {
    return typeof status === "string" ? status.toLowerCase() : undefined
}

const CallSetup: React.FC = () => {
    const { invitationId } = useParams<{ invitationId: string }>()
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const {
        stream,
        setStream,
        isCameraOn,
        setIsCameraOn,
        isMicOn,
        setIsMicOn,
        videoRef,
        availableCameras,
        setAvailableCameras,
        availableMicrophones,
        setAvailableMicrophones,
        availableSpeakers,
        setAvailableSpeakers
    } = useMedia()

    const [isCameraStarting, setIsCameraStarting] = useState(false)
    const [isMicStarting, setIsMicStarting] = useState(false)
    const [showMicWarningModal, setShowMicWarningModal] = useState(false)

    const {
        data: invitationData,
        isLoading: isInvitationLoading,
        isFetching: isInvitationFetching,
        isError: isInvitationError,
        // error: invitationError,
        refetch: refetchInvitation,
    } = useCheckAiInterviewInvitationQuery(invitationId as string, {
        skip: !invitationId,
    })

    const invitationStatus = normaliseStatus(invitationData?.status)
    const statusCode = invitationData?.statusCode || invitationData?.status_code
    const isStatusCodeValid = !statusCode || statusCode === 200

    const isInvitationValid = typeof invitationData?.isValid === "boolean"
        ? invitationData.isValid
        : typeof invitationData?.valid === "boolean"
            ? invitationData.valid
            : invitationStatus
                ? ["valid", "active"].includes(invitationStatus)
                : undefined
    const isInvitationExpired = invitationData?.isExpired === true
        || invitationData?.expired === true
        || (invitationStatus ? ["expired", "invalid", "inactive", "revoked", "cancelled", "canceled"].includes(invitationStatus) : false)

    const isCheckingInvitation = isInvitationLoading || isInvitationFetching
    const shouldBlockAccess = !invitationId
        || isInvitationError
        || (!isCheckingInvitation && (isInvitationExpired || isInvitationValid === false || !isStatusCodeValid))

    // const invitationMessage = (!invitationId && "We could not find an interview invitation. Please use the latest link shared with you.")
    //     || (typeof invitationData?.message === "string" && invitationData.message)
    //     || (typeof invitationData?.reason === "string" && invitationData.reason)
    //     || extractErrorMessage(invitationError)
    //     || (isInvitationExpired ? "This interview invitation has expired. Please contact your recruiter to request a new link." : undefined)
    //     || (!isStatusCodeValid ? "Unable to validate your invitation. Please contact your recruiter for assistance." : undefined)

    const jobTitle = invitationData?.jobTitle || invitationData?.job?.title || "Interview"
    const canRetry = Boolean(invitationId && isInvitationError)
    const userName = invitationData?.firstName ? invitationData?.firstName + ' ' + invitationData?.lastName : 'User Name'

    useEffect(() => {
        if (!invitationId) return
        try {
            localStorage.setItem('invitationId', invitationId)
        } catch (error) {
        }
    }, [invitationId])

    // Save jobTitle to Redux when invitation data is loaded
    useEffect(() => {
        if (invitationData && jobTitle) {
            dispatch(setJobTitle(jobTitle))
            dispatch(setUserName(userName))
        }
    }, [invitationData, jobTitle, dispatch,userName])

    // Auto-start microphone on mount and monitor permission
    useEffect(() => {
        const startMicrophone = async () => {
            if (!isMicOn && !isMicStarting) {
                setIsMicStarting(true)
                try {
                    const newAudioStream = await navigator.mediaDevices.getUserMedia({
                        audio: true,
                        video: false
                    })

                    const audioTrack = newAudioStream.getAudioTracks()[0]

                    // Monitor if microphone track ends (permission revoked)
                    audioTrack.addEventListener('ended', () => {
                        setIsMicOn(false)
                    })

                    if (stream) {
                        stream.addTrack(audioTrack)
                    } else {
                        setStream(newAudioStream)
                    }

                    setIsMicOn(true)

                    // Update device list with labels after permission
                    const devices = await navigator.mediaDevices.enumerateDevices()
                    setAvailableMicrophones(devices.filter(device => device.kind === "audioinput"))
                    setAvailableCameras(devices.filter(device => device.kind === "videoinput"))
                    setAvailableSpeakers(devices.filter(device => device.kind === "audiooutput"))
                } catch (error) {
                } finally {
                    setIsMicStarting(false)
                }
            }
        }
        startMicrophone()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Get available devices on mount (without starting camera)
    useEffect(() => {
        const getDevices = async () => {
            try {
                const devices = await navigator.mediaDevices.enumerateDevices()
                setAvailableCameras(devices.filter(device => device.kind === "videoinput"))
                setAvailableMicrophones(devices.filter(device => device.kind === "audioinput"))
                setAvailableSpeakers(devices.filter(device => device.kind === "audiooutput"))
            } catch (error) {
            }
        }
        getDevices()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Start/Stop Camera
    const toggleCamera = async () => {
        if (isCameraOn) {
            // Turn off camera (stop track completely to turn off camera light)
            if (stream) {
                const videoTracks = stream.getVideoTracks()
                if (videoTracks.length > 0) {
                    videoTracks[0].stop()
                    // Remove the track from stream
                    stream.removeTrack(videoTracks[0])
                    if (stream.getVideoTracks().length === 0) {
                        if (stream.getTracks().length === 0) {
                            setStream(null)
                        }
                        if (videoRef.current) {
                            videoRef.current.srcObject = null
                        }
                    }
                    setIsCameraOn(false)
                }
            }
        } else {
            // Turn on camera - always get new media stream
            setIsCameraStarting(true)
            try {
                const newVideoStream = await navigator.mediaDevices.getUserMedia({
                    video: true,
                    audio: false
                })

                const videoTrack = newVideoStream.getVideoTracks()[0]

                // Add to existing stream or create new one
                if (stream) {
                    stream.addTrack(videoTrack)
                    if (videoRef.current) {
                        videoRef.current.srcObject = stream
                    }
                } else {
                    setStream(newVideoStream)
                    if (videoRef.current) {
                        videoRef.current.srcObject = newVideoStream
                    }
                }

                setIsCameraOn(true)

                // Update device list with labels after permission
                const devices = await navigator.mediaDevices.enumerateDevices()
                setAvailableCameras(devices.filter(device => device.kind === "videoinput"))
                setAvailableMicrophones(devices.filter(device => device.kind === "audioinput"))
                setAvailableSpeakers(devices.filter(device => device.kind === "audiooutput"))
            } catch (error) {
                message.error("Unable to access camera")
            } finally {
                setIsCameraStarting(false)
            }
        }
    }

    // Toggle Microphone - المايك لا يمكن إغلاقه
    const toggleMicrophone = async () => {
        if (isMicOn) {
            // لا يمكن إغلاق المايك - إظهار رسالة تحذيرية
            setShowMicWarningModal(true)
        } else {
            // Turn on microphone - always get new media stream
            setIsMicStarting(true)
            try {
                const newAudioStream = await navigator.mediaDevices.getUserMedia({
                    audio: true,
                    video: false
                })

                const audioTrack = newAudioStream.getAudioTracks()[0]

                // Monitor if microphone track ends (permission revoked)
                audioTrack.addEventListener('ended', () => {
                    setIsMicOn(false)
                })

                // Add to existing stream or create new one
                if (stream) {
                    stream.addTrack(audioTrack)
                } else {
                    setStream(newAudioStream)
                }

                setIsMicOn(true)

                // Update device list with labels after permission
                const devices = await navigator.mediaDevices.enumerateDevices()
                setAvailableMicrophones(devices.filter(device => device.kind === "audioinput"))
            } catch (error) {
                if ((error as any)?.name === 'NotAllowedError' || (error as any)?.name === 'PermissionDeniedError') {
                    message.error("Microphone permission denied. Please allow microphone access in your browser.")
                } else {
                    message.error("Unable to access microphone. Please check your browser settings.")
                }
            } finally {
                setIsMicStarting(false)
            }
        }
    }

    // Change camera
    const handleCameraChange = async (deviceId: string) => {
        if (stream && isCameraOn) {
            // Stop old video tracks
            const oldVideoTracks = stream.getVideoTracks()
            oldVideoTracks.forEach(track => {
                track.stop()
                stream.removeTrack(track)
            })

            try {
                // Get new video stream
                const newVideoStream = await navigator.mediaDevices.getUserMedia({
                    video: { deviceId: { exact: deviceId } },
                    audio: false
                })

                // Add new video track to existing stream
                const newVideoTrack = newVideoStream.getVideoTracks()[0]
                stream.addTrack(newVideoTrack)

                // Update video element
                if (videoRef.current) {
                    videoRef.current.srcObject = stream
                }
            } catch (error) {
                message.error("Unable to change camera")
            }
        }
    }

    // Join call handler
    const handleJoinCall = async () => {
        // Check if microphone is enabled
        if (!isMicOn) {
            message.error("Please enable your microphone to join the interview.")
            // Try to request microphone permission
            try {
                await toggleMicrophone()
            } catch (error) {
            }
            return
        }

        // Verify microphone is still accessible
        try {
            const testStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false })
            testStream.getTracks().forEach(track => track.stop())
        } catch (error) {
            setIsMicOn(false)
            message.error("Microphone permission is required. Please allow microphone access in your browser to join the interview.")
            return
        }

        try {
            const ds: MediaStream = await (navigator.mediaDevices as any).getDisplayMedia({
                video: { frameRate: 30 },
                audio: true,
                preferCurrentTab: true,
            })
                ; (window as any).__cqDisplayStream = ds
            navigate("/interview-call-v2")
        } catch (e) {
            message.error("Screen share is required to join the call.")
        }
    }

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            // Don't cleanup stream on unmount - we need it for the call
            // if (stream) {
            //     stream.getTracks().forEach(track => track.stop())
            // }
        }
    }, [stream])

    if (isCheckingInvitation) {
        return (
            <div className='wrapper'>
                <div className='mt-20'>
                    <LazyLoader />
                    <p className='mt-4 text-base font-normal text-indigo-600'>Checking invitation...</p>
                </div>
            </div>
        )
    }

    if (shouldBlockAccess) {
        return (
            <InvitationNotValid
                // message={invitationMessage}
                showRetry={canRetry}
                onRetry={canRetry ? () => refetchInvitation() : undefined}
            />
        )
    }

    const cameraMenu = (
        <Menu onClick={({ key }) => handleCameraChange(key)}>
            {availableCameras.map(camera => (
                <Menu.Item key={camera.deviceId}>
                    {camera.label || `Camera ${camera.deviceId.slice(0, 8)}`}
                </Menu.Item>
            ))}
        </Menu>
    )

    const handleMicrophoneChange = async (deviceId: string) => {
        if (stream && isMicOn) {
            // Stop old audio tracks
            const oldAudioTracks = stream.getAudioTracks()
            oldAudioTracks.forEach(track => {
                track.stop()
                stream.removeTrack(track)
            })

            try {
                // Get new audio stream
                const newAudioStream = await navigator.mediaDevices.getUserMedia({
                    audio: { deviceId: { exact: deviceId } },
                    video: false
                })

                const newAudioTrack = newAudioStream.getAudioTracks()[0]

                // Monitor if microphone track ends (permission revoked)
                newAudioTrack.addEventListener('ended', () => {
                    setIsMicOn(false)
                })

                stream.addTrack(newAudioTrack)
            } catch (error) {
                if ((error as any)?.name === 'NotAllowedError' || (error as any)?.name === 'PermissionDeniedError') {
                    message.error("Microphone permission denied. Please allow microphone access in your browser.")
                } else {
                    message.error("Unable to change microphone. Please try again.")
                }
            }
        }
    }

    const microphoneMenu = (
        <Menu onClick={({ key }) => handleMicrophoneChange(key)}>
            {availableMicrophones.map(mic => (
                <Menu.Item key={mic.deviceId}>
                    {mic.label || `Microphone ${mic.deviceId.slice(0, 8)}`}
                </Menu.Item>
            ))}
        </Menu>
    )

    const speakerMenu = (
        <Menu>
            {availableSpeakers.map(speaker => (
                <Menu.Item key={speaker.deviceId}>
                    {speaker.label || `Speaker ${speaker.deviceId.slice(0, 8)}`}
                </Menu.Item>
            ))}
        </Menu>
    )

    return (
        <div className="flex flex-col min-h-screen bg-gray-50">
            <header className="flex flex-wrap items-center justify-between px-6 py-4">
                <div className="flex items-center gap-4 mb-2">
                    <div className="flex items-center gap-2">
                        <span className="text-2xl font-bold text-gray-900">people</span>
                        <img src={logo} alt="logo" width={32} height={32} />
                    </div>
                    <span className="text-sm text-gray-500">v2.x</span>
                </div>

                <div className="flex items-center gap-6">
                    <span className="text-sm text-gray-600">{jobTitle}</span>
                    <Divider type="vertical" className="text-gray-700 border-gray-500" />
                    <QuestionCircleOutlined className="text-lg text-gray-400 cursor-pointer" />
                </div>
            </header>

            <main className="flex flex-col-reverse items-center justify-center flex-1 gap-5 p-8 mx-auto lg:flex-row md:gap-28">
                {/* Video section */}
                <div className="flex flex-col gap-[0.625rem]">
                    <div className="flex flex-col gap-4">
                        <div className="relative overflow-hidden bg-gray-900 shadow-lg rounded-2xl w-full md:w-[42rem] h-96">
                            <video
                                ref={videoRef}
                                autoPlay
                                playsInline
                                muted
                                style={{ transform: "scaleX(-1)" }}
                                className="object-cover w-full h-full"
                            />
                            {!isCameraOn && (
                                <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-gray-700">
                                    <img
                                        src={`https://ui-avatars.com/api/?name=${userName}&size=200&background=4338CA&color=fff&bold=true`}
                                        alt="Camera Off"
                                        className="rounded-full opacity-50"
                                    />
                                    {isCameraStarting && (
                                        <p className="text-lg text-white animate-pulse">Camera starting...</p>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="flex flex-wrap items-center justify-center gap-5 md:gap-32">
                            <div className="flex items-center">
                                <Button
                                    type="text"
                                    onClick={toggleCamera}
                                    disabled={isCameraStarting}
                                    className={`px-2 flex items-center gap-1 text-gray-500`}
                                >
                                    {isCameraOn ? <CameraOutlined className="text-base" /> : <MutedCameraOutlined className="text-base" />}
                                    <span className="text-xs font-light">
                                        {isCameraStarting ? 'Camera starting...' : isCameraOn ? 'Camera On' : 'Camera Off'}
                                    </span>
                                </Button>
                                <Dropdown overlay={cameraMenu} trigger={['click']} disabled={!isCameraOn}>
                                    <Button type="text" className="flex justify-center p-0 text-gray-600" disabled={!isCameraOn}>
                                        <MoreOutlined className="text-sm rotate-90" />
                                    </Button>
                                </Dropdown>
                            </div>

                            <div className="flex items-center gap-2">
                                <Button
                                    type="text"
                                    onClick={toggleMicrophone}
                                    disabled={isMicStarting}
                                    className={`px-2 flex items-center gap-2 text-gray-500`}
                                >
                                    {isMicOn ? <AudioOutlined className="text-base" /> : <AudioMutedOutlined className="text-base" />}
                                    <span className="text-xs font-light">
                                        {isMicStarting ? 'Mic starting...' : isMicOn ? 'Mic On' : 'Mic Off'}
                                    </span>
                                </Button>
                                <Dropdown overlay={microphoneMenu} trigger={['click']} disabled={!isMicOn}>
                                    <Button type="text" className="flex justify-center p-0 text-gray-600" disabled={!isMicOn}>
                                        <MoreOutlined className="text-sm rotate-90" />
                                    </Button>
                                </Dropdown>
                            </div>

                            <div className="flex items-center">
                                <Button type="text" className="flex items-center gap-2 px-2 text-gray-500">
                                    <SoundOutlined className="text-base" />
                                    <span className="text-xs font-light">Audio speakers</span>
                                </Button>
                                <Dropdown overlay={speakerMenu} trigger={['click']}>
                                    <Button type="text" className="flex justify-center text-gray-500">
                                        <MoreOutlined className="text-sm text-gray-500 rotate-90" />
                                    </Button>
                                </Dropdown>
                            </div>
                        </div>
                    </div>
                    <p className="text-sm font-normal text-center text-gray-500">
                        <span className="font-semibold">Allow screen sharing before we start.</span><br />
                        DO NOT close your shared screen during the interview, or the session will end automatically.
                    </p>
                </div>

                <div className="flex flex-col items-center gap-6">
                    <h2 className="text-2xl font-semibold text-gray-900">Ready to Join?</h2>

                    <div className="flex flex-col items-center gap-3">
                        <div className="w-20 h-20 overflow-hidden rounded-lg md:w-28 md:h-28 md:top-auto md:bottom-8 md:right-8">
                            <AnimatedSpeaking isSpeaking={false} />
                        </div>
                        <p className="text-sm text-gray-600">Myra is on the call</p>
                    </div>

                    {!isMicOn && !isMicStarting && (
                        <div className="px-4 py-3 text-center border-l-4 border-red-500 rounded-r-lg bg-red-50">
                            <p className="text-sm font-semibold text-red-800">
                                🎤 Microphone Required
                            </p>
                            <p className="mt-1 text-xs text-red-600">
                                Please allow microphone access in your browser to join
                            </p>
                        </div>
                    )}

                    <Button
                        type="primary"
                        size="large"
                        onClick={handleJoinCall}
                        disabled={!isMicOn || isMicStarting}
                        className={`h-12 px-16 font-medium ${!isMicOn || isMicStarting
                            ? 'bg-gray-400 cursor-not-allowed'
                            : 'bg-indigo-600 hover:bg-indigo-700'
                            }`}
                    >
                        {isMicStarting ? 'Starting Microphone...' : !isMicOn ? 'Microphone Required' : 'Join Call'}
                    </Button>
                </div>
            </main>

            {/* Microphone Required Modal */}
            <ModalComponent
                visibleModal={showMicWarningModal}
                handleCloseModal={() => setShowMicWarningModal(false)}
                title=""
                width={384}
                className="mic-warning-modal"
                content={
                    <div className="flex flex-col items-center gap-6">
                        <div className="flex flex-col gap-5">
                            <div className="flex items-center justify-center w-12 h-12 mx-auto bg-indigo-100 rounded-full">
                                <Microphone className="w-6 h-6" />
                            </div>
                            <div className="flex flex-col items-center justify-center w-full gap-2">
                                <h3 className="text-lg font-medium leading-6 text-gray-900">
                                    Microphone Required
                                </h3>
                                <p className="text-sm leading-relaxed text-center text-gray-500">
                                    Your microphone must be active before and throughout the interview to ensure clear communication and a smooth interview flow.
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 px-[0.625rem] py-2 rounded-md bg-yellow-50">
                            <div>

                                <Lightbulb className="!w-5 !h-5" />
                            </div>
                            <p className="text-xs text-amber-800">
                                This helps our Al interviewer hear and respond to your answers accurately.
                            </p>
                        </div>
                        <Button
                            type="primary"
                            size="large"
                            onClick={() => setShowMicWarningModal(false)}
                            className="w-full !h-14 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium"
                        >
                            Ok
                        </Button>
                    </div>
                }
            />
        </div>
    )
}

export default CallSetup
